import { array, lazy, mixed, object, ObjectSchema, string } from 'yup';

import {
  DataConfiguration,
  DataConfigurationType,
  IArrayDataConfiguration,
  IObjectDataConfiguration,
  IPrimitiveDataConfiguration,
} from '../types/data-configuration.types';

const baseConfigurationSchema = object()
  .shape({
    description: string().trim().notRequired(),
  })
  .noUnknown();

export const primitiveDataConfigurationSchema = baseConfigurationSchema.clone().shape({
  type: mixed<IPrimitiveDataConfiguration['type']>()
    .oneOf(
      Object.values(DataConfigurationType).filter(
        (t) => ![DataConfigurationType.Array, DataConfigurationType.Object].includes(t)
      ) as any
    )
    .required(),
});

export const arrayDataConfigurationSchema = baseConfigurationSchema.clone().shape({
  items: array().when('type', {
    is: (val: any) => val === DataConfigurationType.Array,
    then: () => lazy(() => primitiveDataConfigurationSchema.required()),
    otherwise: () => mixed().oneOf([undefined], 'items must be undefined'),
  }),
  type: mixed<IArrayDataConfiguration['type']>().oneOf([DataConfigurationType.Array]).required(),
});

export const objectDataConfigurationSchema = baseConfigurationSchema.clone().shape({
  properties: mixed<any>().when('type', {
    is: (val: any) => val === DataConfigurationType.Object,
    then: () =>
      lazy((properties: Record<string, DataConfiguration>) => {
        const mapped = Object.keys(properties ?? {})
          .map((key) => ({ [key]: dataConfigurationSchema.required() }))
          .reduce((acc, value) => ({ ...acc, ...value }), {});

        return object(mapped)
          .required()
          .test({
            name: 'notEmpty',
            message: 'object must not be empty',
            test: (value) => Object.keys(value || {}).length > 0,
          });
      }),
    otherwise: () => mixed().oneOf([undefined], 'properties must be undefined'),
  }),
  type: mixed<IObjectDataConfiguration['type']>().oneOf([DataConfigurationType.Object]).required(),
});

export const dataConfigurationSchema: ObjectSchema<DataConfiguration> = arrayDataConfigurationSchema
  .concat(objectDataConfigurationSchema as ObjectSchema<IObjectDataConfiguration>)
  .concat(primitiveDataConfigurationSchema as ObjectSchema<IPrimitiveDataConfiguration>)
  .shape({
    type: mixed().oneOf(Object.values(DataConfigurationType)).required() as any,
  }) as any;
